import React from "react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { Shield } from "lucide-react";

const SECTIONS = [
  { title:"1. Information We Collect", items:[
    ["Account Information","Name, email, phone, role, and professional credentials including PPCA/NPCA certification numbers and state licenses."],
    ["Vehicle & Equipment","Year, make, model, color, license plate, escort equipment certifications, and insurance documentation."],
    ["Payment Information","Processed by Stripe. PilotLink USA does not store full card numbers — only billing name, last 4 digits, and transaction history."],
    ["Usage Data","IP addresses, browser type, pages visited, time spent, and actions taken on the platform."],
    ["GPS & Location","Active during dispatch operations only, with explicit consent. Not collected during non-active sessions."],
  ]},
  { title:"2. How We Use Your Information", items:[
    ["Platform Operations","Account management, payment processing, load matching, certification verification, and dispatch operations."],
    ["Verified Badge","Your PPCA/NPCA certification data generates your Verified Badge for carrier scanning."],
    ["Communications","OTP codes, payment receipts, badge awards, and platform updates. Marketing requires opt-in."],
    ["Safety & Compliance","Verify credentials before load matching and maintain industry safety standards across all 50 states."],
  ]},
  { title:"3. Information Sharing", items:[
    ["With Carriers","Verified profile (name, badge, certifications, ratings) visible to load-posting companies. Full contact shared only on confirmed match."],
    ["With Stripe","Payment data subject to Stripe's Privacy Policy (stripe.com/privacy). We share only what is needed to process transactions."],
    ["Legal Disclosure","May disclose information if required by law, court order, or to protect user and public safety."],
    ["Service Providers","Email, hosting, analytics providers under strict confidentiality agreements."],
  ]},
  { title:"4. Data Security", items:[
    ["Encryption","All data transmitted over TLS. Passwords hashed with industry-standard algorithms. Payment tokenized by Stripe."],
    ["Access Controls","Role-based employee access, logged, and restricted to job function requirements."],
    ["Breach Notification","Affected users notified within 72 hours of a confirmed data breach."],
  ]},
  { title:"5. Your Rights", items:[
    ["Access","Request a copy of all personal data by emailing support@pilotlinkusa.com."],
    ["Correction","Update account information through profile settings at any time."],
    ["Deletion","Request account deletion. Transaction records may be retained as required by law."],
    ["Opt-Out","Unsubscribe from marketing via email link or notification settings."],
  ]},
  { title:"6. Cookies & Advertising", items:[
    ["Essential Cookies","Authentication sessions, security tokens, user preferences."],
    ["Analytics","Help us improve the platform. May be disabled through browser settings."],
    ["No Ads","PilotLink USA products are completely ad-free. We do not sell your data."],
  ]},
  { title:"7. Contact & Updates", items:[
    ["Policy Updates","Material changes notified by email and platform notice 30 days before taking effect."],
    ["Contact","privacy@pilotlinkusa.com | support@pilotlinkusa.com"],
  ]},
];

export default function PrivacyPolicy({ isDarkMode, toggleTheme }) {
  const D      = isDarkMode;
  const bg     = D ? "#0d1f3c" : "#f4f7fb";
  const panel  = D ? "#142a4d" : "#ffffff";
  const mid    = D ? "#1a3358" : "#f0f4f8";
  const border = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text   = D ? "#f0f4f8" : "#0d1f3c";
  const textMid= D ? "rgba(240,244,248,0.80)" : "#334155";
  const textSub= D ? "rgba(240,244,248,0.55)" : "#64748b";
  const gold   = "#c9971c";

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"'Inter',sans-serif", transition:"background .3s" }}>
      <Navbar isDarkMode={D} toggleTheme={toggleTheme}/>
      <div style={{ background:"linear-gradient(135deg,#0d1f3c,#1a3358)", padding:"clamp(40px,6vw,68px) 16px", textAlign:"center", borderBottom:`3px solid ${gold}` }}>
        <div style={{ width:52, height:52, borderRadius:13, background:gold, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <Shield size={26} style={{ color:"#0d1f3c" }}/>
        </div>
        <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(24px,4vw,38px)", fontWeight:900, color:"#fff", margin:"0 0 10px" }}>Privacy Policy</h1>
        <p style={{ fontSize:13, color:"rgba(255,255,255,0.50)", margin:0 }}>Effective July 1, 2026 · Last Updated July 2, 2026</p>
      </div>
      <div style={{ maxWidth:820, margin:"0 auto", padding:"clamp(24px,4vw,44px) clamp(14px,3vw,24px)" }}>
        <div style={{ background:panel, borderRadius:14, padding:"clamp(18px,2.5vw,24px)", marginBottom:20, border:`1px solid ${border}` }}>
          <p style={{ fontSize:15, color:textMid, lineHeight:1.85, margin:0 }}>
            PilotLink USA is committed to protecting the privacy of pilot car operators, companies, and dispatchers. This policy explains what we collect, how we use it, and your rights.
          </p>
        </div>
        {SECTIONS.map((sec, i) => (
          <div key={i} style={{ background:panel, borderRadius:14, padding:"clamp(18px,2.5vw,24px)", marginBottom:12, border:`1px solid ${border}` }}>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(15px,2vw,18px)", fontWeight:800, color:gold, margin:"0 0 16px", paddingBottom:10, borderBottom:`1px solid ${border}` }}>{sec.title}</h2>
            <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
              {sec.items.map(([h, t], j) => (
                <div key={j}>
                  <div style={{ fontSize:13.5, fontWeight:700, color:text, marginBottom:4 }}>{h}</div>
                  <p style={{ fontSize:13.5, color:textMid, lineHeight:1.80, margin:0 }}>{t}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div style={{ background:mid, borderRadius:10, padding:"14px 18px", border:`1px solid ${border}`, textAlign:"center" }}>
          <p style={{ fontSize:13, color:textSub, margin:0 }}>Questions? <a href="mailto:privacy@pilotlinkusa.com" style={{ color:gold, fontWeight:600 }}>privacy@pilotlinkusa.com</a></p>
        </div>
      </div>
      <Footer isDarkMode={D}/>
    </div>
  );
}
